import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  NewReleases as NewReleasesIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

/**
 * Changelog Modal Component
 *
 * Displays version history and changes in a modal dialog.
 * Can be triggered manually or automatically on login after version update.
 */
const ChangelogModal = ({ open, onClose, autoOpened = false }) => {
  const { authFetch } = useAuth();
  const [changelogs, setChangelogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (open) {
      fetchChangelogs();
    }
  }, [open]);

  const fetchChangelogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/changelog?limit=5');
      const data = await response.json();

      if (response.ok) {
        setChangelogs(data.data || []);
        // Auto-expand the first (latest) version
        if (data.data && data.data.length > 0) {
          setExpanded(data.data[0].version);
        }
      } else {
        setError(data.message || 'Fehler beim Laden des Changelogs');
      }
    } catch (err) {
      setError('Netzwerkfehler beim Laden des Changelogs');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    // Mark latest version as seen when closing
    if (autoOpened && changelogs.length > 0) {
      try {
        await authFetch('/api/changelog/mark-seen', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ version: changelogs[0].version })
        });
      } catch (err) {
        console.error('Error marking version as seen:', err);
      }
    }

    onClose();
  };

  const handleAccordionChange = (version) => (event, isExpanded) => {
    setExpanded(isExpanded ? version : null);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Hinzugefügt': 'success',
      'Geändert': 'primary',
      'Behoben': 'warning',
      'Entfernt': 'error',
      'Veraltet': 'default',
      'Sicherheit': 'error',
      'Technisch': 'info'
    };
    return colors[category] || 'default';
  };

  const renderChange = (change) => {
    if (typeof change === 'string') {
      return <Typography variant="body2" sx={{ mb: 0.5 }}>{change}</Typography>;
    }

    // Change is an object with title, description, and items
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {change.title}: <span style={{ fontWeight: 400 }}>{change.description}</span>
        </Typography>
        {change.items && change.items.length > 0 && (
          <Box sx={{ pl: 2 }}>
            {change.items.map((item, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{ mb: 0.3, color: 'text.secondary', fontSize: '0.875rem' }}
              >
                • {item}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const renderChanges = (changes) => {
    return Object.entries(changes).map(([category, items]) => {
      if (!items || items.length === 0) return null;

      return (
        <Box key={category} sx={{ mb: 2 }}>
          <Chip
            label={category}
            color={getCategoryColor(category)}
            size="small"
            sx={{ mb: 1, fontWeight: 600 }}
          />
          <Box sx={{ pl: 1 }}>
            {items.map((change, idx) => (
              <Box key={idx}>{renderChange(change)}</Box>
            ))}
          </Box>
        </Box>
      );
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NewReleasesIcon color="primary" />
          <Typography variant="h6">Changelog</Typography>
        </Box>
        {autoOpened && changelogs.length > 0 && (
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
            Neu in Version {changelogs[0].version}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && changelogs.length === 0 && (
          <Alert severity="info">
            Keine Changelog-Einträge verfügbar.
          </Alert>
        )}

        {!loading && !error && changelogs.length > 0 && (
          <Box>
            {changelogs.map((changelog, index) => (
              <Accordion
                key={changelog.version}
                expanded={expanded === changelog.version}
                onChange={handleAccordionChange(changelog.version)}
                sx={{
                  '&:before': {
                    display: 'none'
                  },
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 1
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: index === 0 ? 'action.hover' : 'transparent',
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 2
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    v{changelog.version}
                  </Typography>
                  <Chip
                    label={new Date(changelog.date).toLocaleDateString('de-DE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    size="small"
                    variant="outlined"
                  />
                  {index === 0 && (
                    <Chip
                      label="Neueste"
                      color="primary"
                      size="small"
                    />
                  )}
                </AccordionSummary>

                <AccordionDetails>
                  <Divider sx={{ mb: 2 }} />
                  {renderChanges(changelog.changes)}
                </AccordionDetails>
              </Accordion>
            ))}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Vollständige Versionshistorie auf der Changelog-Seite verfügbar
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained" startIcon={<CloseIcon />}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangelogModal;
